import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../material/material.module';
import { CurriculumAdminService } from '../services/curriculum-admin.service';

export interface PerkItem {
  icon: string;
  text: string;
}

export interface WhyCardItem {
  icon: string;
  color: string;
  title: string;
  description: string;
}

export interface CoursesPageConfigData {
  heroBadge: string;
  heroTitle: string;
  heroGradientText: string;
  heroSubtitle: string;
  perks: PerkItem[];
  whySectionTag: string;
  whySectionTitle: string;
  whySectionSubtitle: string;
  whyCards: WhyCardItem[];
}

@Component({
  selector: 'app-admin-courses-config-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  template: `
    <div class="dialog-container">
      <!-- HEADER -->
      <div class="dialog-header">
        <div class="title-wrap">
          <div class="header-icon-box">
            <mat-icon>tune</mat-icon>
          </div>
          <div>
            <h2 mat-dialog-title class="dialog-title">Configure Courses Page</h2>
            <p class="dialog-subtitle">Customize hero banners, value perks, and advantage feature cards</p>
          </div>
        </div>
        <button mat-icon-button (click)="cancel()" aria-label="Close dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <!-- BODY TABS -->
      <div mat-dialog-content class="dialog-body">
        <div *ngIf="loading" class="loading-box">
          <mat-spinner diameter="36"></mat-spinner>
          <p>Loading current page configuration...</p>
        </div>

        <mat-tab-group *ngIf="!loading" animationDuration="200ms" class="config-tabs">
          <!-- TAB 1: HERO SECTION -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">view_headline</mat-icon> Hero Banner
            </ng-template>
            <div class="tab-content">
              <div class="preview-hero-card">
                <div class="hero-badge-preview">
                  <mat-icon>school</mat-icon>
                  <span>{{ config.heroBadge || 'Interactive Academics & Digital Textbooks' }}</span>
                </div>
                <h3 class="hero-title-preview">
                  {{ config.heroTitle || 'Explore Courses & Interactive Books' }}
                </h3>
                <p class="hero-sub-preview">{{ config.heroSubtitle || 'Course description...' }}</p>
              </div>

              <div class="form-fields">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Hero Eyebrow / Pill Badge</mat-label>
                  <input matInput [(ngModel)]="config.heroBadge" placeholder="e.g. Interactive Academics & Digital Textbooks" />
                  <mat-hint>Top small pill badge in the hero header</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Hero Main Title</mat-label>
                  <input matInput [(ngModel)]="config.heroTitle" placeholder="e.g. Explore Courses & Interactive Books" required />
                  <mat-hint>Main prominent H1 headline</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Gradient Highlight Phrase (Substring in Title)</mat-label>
                  <input matInput [(ngModel)]="config.heroGradientText" placeholder="e.g. Interactive Books" />
                  <mat-hint>Words in title to style with colorful gradient</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Hero Subtitle / Description</mat-label>
                  <textarea matInput [(ngModel)]="config.heroSubtitle" rows="3" placeholder="Paragraph describing the course offerings..."></textarea>
                </mat-form-field>
              </div>
            </div>
          </mat-tab>

          <!-- TAB 2: VALUE PERKS -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">stars</mat-icon> Value Perks ({{ config.perks.length }})
            </ng-template>
            <div class="tab-content">
              <div class="perks-toolbar">
                <span class="section-label">Highlight chips shown directly beneath the hero subtitle:</span>
                <button mat-stroked-button color="primary" (click)="addPerk()">
                  <mat-icon>add</mat-icon> Add Perk
                </button>
              </div>

              <div class="perks-list">
                <div *ngFor="let perk of config.perks; let i = index" class="perk-edit-row">
                  <div class="perk-index-badge">#{{ i + 1 }}</div>
                  
                  <mat-form-field appearance="outline" class="perk-icon-field">
                    <mat-label>Icon</mat-label>
                    <input matInput [(ngModel)]="perk.icon" placeholder="lock_open" />
                    <mat-icon matSuffix>{{ perk.icon || 'star' }}</mat-icon>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="perk-text-field">
                    <mat-label>Perk Text</mat-label>
                    <input matInput [(ngModel)]="perk.text" placeholder="e.g. Free Preview on All Books" />
                  </mat-form-field>

                  <button mat-icon-button color="warn" (click)="removePerk(i)" [disabled]="config.perks.length <= 1" matTooltip="Remove perk">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>

              <!-- Quick Icon Picker -->
              <div class="quick-icons-box">
                <span class="quick-icons-label">Popular Perk Icons:</span>
                <div class="quick-icons-row">
                  <button *ngFor="let ic of popularPerkIcons" type="button" class="mini-icon-btn" (click)="applyIconToSelectedPerk(ic)">
                    <mat-icon>{{ ic }}</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </mat-tab>

          <!-- TAB 3: WHY CHOOSE SECTION -->
          <mat-tab>
            <ng-template mat-tab-label>
              <mat-icon class="tab-icon">dashboard_customize</mat-icon> Why Choose Cards
            </ng-template>
            <div class="tab-content">
              <div class="form-fields section-header-fields">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Section Eyebrow Tag</mat-label>
                  <input matInput [(ngModel)]="config.whySectionTag" placeholder="e.g. THE KSCOURSES ADVANTAGE" />
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Section Heading</mat-label>
                  <input matInput [(ngModel)]="config.whySectionTitle" placeholder="e.g. Why Students & Parents Love Our Interactive Books" />
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Section Subtitle</mat-label>
                  <input matInput [(ngModel)]="config.whySectionSubtitle" placeholder="e.g. Built for clarity, speed, and real mastery. No boring PDFs—just high-engagement learning." />
                </mat-form-field>
              </div>

              <div class="cards-header-row">
                <span class="section-label">4 Advantage Feature Cards:</span>
              </div>

              <div class="why-cards-edit-grid">
                <div *ngFor="let card of config.whyCards; let i = index" class="why-card-edit-box">
                  <div class="card-edit-top">
                    <span class="card-num-pill">Card #{{ i + 1 }}</span>
                    <div class="color-picker-mini">
                      <button *ngFor="let c of cardColors" type="button" class="color-dot" [style.background]="c" [class.selected]="card.color === c" (click)="card.color = c"></button>
                    </div>
                  </div>

                  <div class="card-inputs">
                    <div class="icon-input-row">
                      <mat-form-field appearance="outline" class="card-icon-field">
                        <mat-label>Icon</mat-label>
                        <input matInput [(ngModel)]="card.icon" placeholder="slideshow" />
                        <mat-icon matSuffix [style.color]="card.color">{{ card.icon || 'star' }}</mat-icon>
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="card-title-field">
                        <mat-label>Card Title</mat-label>
                        <input matInput [(ngModel)]="card.title" placeholder="Visual Page-by-Page Lessons" />
                      </mat-form-field>
                    </div>

                    <mat-form-field appearance="outline" class="full-width">
                      <mat-label>Card Description</mat-label>
                      <textarea matInput [(ngModel)]="card.description" rows="2" placeholder="Step-by-step illustrated concept pages..."></textarea>
                    </mat-form-field>
                  </div>
                </div>
              </div>
            </div>
          </mat-tab>
        </mat-tab-group>
      </div>

      <mat-divider></mat-divider>

      <!-- FOOTER ACTIONS -->
      <div mat-dialog-actions class="dialog-actions">
        <button mat-button type="button" color="warn" (click)="resetToDefaults()" [disabled]="saving">
          <mat-icon>restart_alt</mat-icon> Reset to Defaults
        </button>
        <span class="spacer"></span>
        <button mat-button type="button" (click)="cancel()" [disabled]="saving">Cancel</button>
        <button mat-raised-button color="primary" type="button" (click)="save()" [disabled]="saving">
          <mat-spinner diameter="18" *ngIf="saving"></mat-spinner>
          <mat-icon *ngIf="!saving">save</mat-icon>
          <span>Save Changes</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 680px;
      max-width: 820px;
    }
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.25rem 1.75rem;
      background: #fafafa;
    }
    .title-wrap {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .header-icon-box {
      background: linear-gradient(135deg, #1e293b, #0f172a);
      color: #38bdf8;
      height: 48px;
      width: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 10px rgba(0,0,0,0.15);
    }
    .header-icon-box mat-icon { font-size: 26px; height: 26px; width: 26px; }
    .dialog-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 700;
      color: #1e293b;
    }
    .dialog-subtitle {
      margin: 0.2rem 0 0 0;
      font-size: 0.85rem;
      color: #64748b;
    }
    .dialog-body {
      padding: 1.25rem 1.75rem;
      max-height: 70vh;
      overflow-y: auto;
    }
    .loading-box {
      text-align: center;
      padding: 3rem 1rem;
      color: #64748b;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
    }
    .tab-icon {
      margin-right: 0.4rem;
      font-size: 1.1rem;
      height: 1.1rem;
      width: 1.1rem;
    }
    .tab-content {
      padding: 1.25rem 0 0.5rem 0;
    }

    /* PREVIEW HERO CARD */
    .preview-hero-card {
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      color: white;
      padding: 1.5rem;
      border-radius: 12px;
      margin-bottom: 1.25rem;
      text-align: center;
    }
    .hero-badge-preview {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      background: rgba(255, 255, 255, 0.1);
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      color: #93c5fd;
      margin-bottom: 0.5rem;
    }
    .hero-badge-preview mat-icon { font-size: 14px; height: 14px; width: 14px; }
    .hero-title-preview {
      font-size: 1.35rem;
      font-weight: 800;
      margin: 0.25rem 0;
    }
    .hero-sub-preview {
      font-size: 0.85rem;
      color: #cbd5e1;
      margin: 0;
      line-height: 1.4;
    }

    .form-fields {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .full-width { width: 100%; }

    /* PERKS TAB */
    .perks-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }
    .section-label {
      font-size: 0.85rem;
      font-weight: 600;
      color: #475569;
    }
    .perks-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin-bottom: 1.25rem;
    }
    .perk-edit-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      background: #f8fafc;
      padding: 0.5rem 0.75rem;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
    }
    .perk-index-badge {
      font-size: 0.8rem;
      font-weight: 700;
      color: #1d4ed8;
      background: #eff6ff;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
    }
    .perk-icon-field {
      width: 140px;
      margin-bottom: -1.25em;
    }
    .perk-text-field {
      flex: 1;
      margin-bottom: -1.25em;
    }

    .quick-icons-box {
      background: #f1f5f9;
      padding: 0.75rem 1rem;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
    .quick-icons-label {
      font-size: 0.8rem;
      font-weight: 600;
      color: #475569;
    }
    .quick-icons-row {
      display: flex;
      gap: 0.4rem;
      flex-wrap: wrap;
    }
    .mini-icon-btn {
      height: 32px;
      width: 32px;
      border-radius: 6px;
      border: 1px solid #cbd5e1;
      background: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #334155;
      transition: all 0.15s;
    }
    .mini-icon-btn:hover {
      background: #1976d2;
      color: white;
      border-color: #1976d2;
    }
    .mini-icon-btn mat-icon { font-size: 18px; height: 18px; width: 18px; }

    /* WHY CARDS TAB */
    .section-header-fields {
      margin-bottom: 1.25rem;
      padding-bottom: 1rem;
      border-bottom: 1px dashed #cbd5e1;
    }
    .cards-header-row {
      margin-bottom: 0.75rem;
    }
    .why-cards-edit-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }
    .why-card-edit-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 1rem;
    }
    .card-edit-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;
    }
    .card-num-pill {
      font-size: 0.75rem;
      font-weight: 700;
      color: #475569;
      background: #e2e8f0;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
    }
    .color-picker-mini {
      display: flex;
      gap: 0.35rem;
    }
    .color-dot {
      height: 20px;
      width: 20px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 0 0 1px #cbd5e1;
      cursor: pointer;
      transition: transform 0.15s;
    }
    .color-dot:hover { transform: scale(1.2); }
    .color-dot.selected { box-shadow: 0 0 0 2px #0f172a; }

    .card-inputs {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .icon-input-row {
      display: flex;
      gap: 0.5rem;
    }
    .card-icon-field {
      width: 120px;
      margin-bottom: -1.25em;
    }
    .card-title-field {
      flex: 1;
      margin-bottom: -1.25em;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.75rem;
      background: #fafafa;
    }
    .spacer { flex: 1; }
  `]
})
export class AdminCoursesConfigDialogComponent implements OnInit {
  loading = true;
  saving = false;

  popularPerkIcons = ['lock_open', 'headset', 'style', 'verified', 'auto_stories', 'timer', 'school', 'psychology', 'emoji_events', 'devices'];
  cardColors = ['#1976d2', '#00897b', '#e65100', '#6a1b9a', '#d32f2f', '#0284c7'];

  config: CoursesPageConfigData = {
    heroBadge: 'Interactive Academics & Digital Textbooks',
    heroTitle: 'Explore Courses & Interactive Books',
    heroGradientText: 'Interactive Books',
    heroSubtitle: 'Master Pre-Algebra, Physics, Computer Science, and more with animated lesson pages, crystal-clear audio narrations, spaced-repetition flashcards, and instant-graded quizzes.',
    perks: [
      { icon: 'lock_open', text: 'Free Preview on All Books' },
      { icon: 'headset', text: 'Audio-Guided Lessons' },
      { icon: 'style', text: 'Interactive Flashcards' },
      { icon: 'verified', text: 'Instant Quiz Grading' }
    ],
    whySectionTag: 'THE KSCOURSES ADVANTAGE',
    whySectionTitle: 'Why Students & Parents Love Our Interactive Books',
    whySectionSubtitle: 'Built for clarity, speed, and real mastery. No boring PDFs—just high-engagement learning.',
    whyCards: [
      {
        icon: 'slideshow',
        color: '#1976d2',
        title: 'Visual Page-by-Page Lessons',
        description: 'Step-by-step illustrated concept pages break down complex mathematical and scientific theories into digestible steps.'
      },
      {
        icon: 'record_voice_over',
        color: '#00897b',
        title: 'Professional Audio Narration',
        description: 'Every page includes synchronized audio playback so auditory and visual learners can follow along effortlessly.'
      },
      {
        icon: 'style',
        color: '#e65100',
        title: 'Active Recall Flashcards',
        description: 'Reinforce essential formulas, definitions, and key problem types with flip cards designed for long-term memory.'
      },
      {
        icon: 'quiz',
        color: '#6a1b9a',
        title: 'Adaptive Practice Quizzes',
        description: 'Test knowledge with multiple-choice quizzes that provide immediate scoring, explanations, and mastery tracking.'
      }
    ]
  };

  constructor(
    public dialogRef: MatDialogRef<AdminCoursesConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private curriculumAdminService: CurriculumAdminService
  ) {}

  ngOnInit() {
    this.loadConfig();
  }

  loadConfig() {
    this.loading = true;
    this.curriculumAdminService.getCoursesPageConfig().subscribe({
      next: (res) => {
        if (res && res.heroTitle) {
          this.config = {
            heroBadge: res.heroBadge ?? this.config.heroBadge,
            heroTitle: res.heroTitle ?? this.config.heroTitle,
            heroGradientText: res.heroGradientText ?? this.config.heroGradientText,
            heroSubtitle: res.heroSubtitle ?? this.config.heroSubtitle,
            perks: Array.isArray(res.perks) && res.perks.length > 0 ? res.perks : this.config.perks,
            whySectionTag: res.whySectionTag ?? this.config.whySectionTag,
            whySectionTitle: res.whySectionTitle ?? this.config.whySectionTitle,
            whySectionSubtitle: res.whySectionSubtitle ?? this.config.whySectionSubtitle,
            whyCards: Array.isArray(res.whyCards) && res.whyCards.length > 0 ? res.whyCards : this.config.whyCards
          };
        }
        this.loading = false;
      },
      error: (err) => {
        console.warn('Could not load existing config from backend, using defaults:', err);
        this.loading = false;
      }
    });
  }

  addPerk() {
    this.config.perks.push({ icon: 'star', text: 'New Value Feature' });
  }

  removePerk(index: number) {
    if (this.config.perks.length > 1) {
      this.config.perks.splice(index, 1);
    }
  }

  applyIconToSelectedPerk(icon: string) {
    if (this.config.perks.length > 0) {
      this.config.perks[this.config.perks.length - 1].icon = icon;
    }
  }

  resetToDefaults() {
    if (confirm('Reset all Courses Page texts and cards back to initial defaults?')) {
      this.config = {
        heroBadge: 'Interactive Academics & Digital Textbooks',
        heroTitle: 'Explore Courses & Interactive Books',
        heroGradientText: 'Interactive Books',
        heroSubtitle: 'Master Pre-Algebra, Physics, Computer Science, and more with animated lesson pages, crystal-clear audio narrations, spaced-repetition flashcards, and instant-graded quizzes.',
        perks: [
          { icon: 'lock_open', text: 'Free Preview on All Books' },
          { icon: 'headset', text: 'Audio-Guided Lessons' },
          { icon: 'style', text: 'Interactive Flashcards' },
          { icon: 'verified', text: 'Instant Quiz Grading' }
        ],
        whySectionTag: 'THE KSCOURSES ADVANTAGE',
        whySectionTitle: 'Why Students & Parents Love Our Interactive Books',
        whySectionSubtitle: 'Built for clarity, speed, and real mastery. No boring PDFs—just high-engagement learning.',
        whyCards: [
          {
            icon: 'slideshow',
            color: '#1976d2',
            title: 'Visual Page-by-Page Lessons',
            description: 'Step-by-step illustrated concept pages break down complex mathematical and scientific theories into digestible steps.'
          },
          {
            icon: 'record_voice_over',
            color: '#00897b',
            title: 'Professional Audio Narration',
            description: 'Every page includes synchronized audio playback so auditory and visual learners can follow along effortlessly.'
          },
          {
            icon: 'style',
            color: '#e65100',
            title: 'Active Recall Flashcards',
            description: 'Reinforce essential formulas, definitions, and key problem types with flip cards designed for long-term memory.'
          },
          {
            icon: 'quiz',
            color: '#6a1b9a',
            title: 'Adaptive Practice Quizzes',
            description: 'Test knowledge with multiple-choice quizzes that provide immediate scoring, explanations, and mastery tracking.'
          }
        ]
      };
    }
  }

  save() {
    if (!this.config.heroTitle || this.config.heroTitle.trim() === '') {
      alert('Hero Main Title is required.');
      return;
    }

    this.saving = true;
    this.curriculumAdminService.saveCoursesPageConfig(this.config).subscribe({
      next: (res) => {
        this.saving = false;
        this.dialogRef.close(this.config);
      },
      error: (err) => {
        this.saving = false;
        alert('Failed to save Courses Page configuration: ' + (err.error?.message || err.message));
      }
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}
